/**
 * Better Auth's client, configured once — behind the eight calls the SPA makes.
 *
 * `STACK.md` §Auth pins the four plugins; three of them have a client half and all
 * three are here. The fourth, `mcp`, is an OAuth 2.1 authorization server for MCP
 * clients: its flow happens in the browser's address bar, and the two calls
 * `/oauth/consent` makes are the only part of it that comes through here.
 *
 * **Those two have no client plugin either**, and do not need one. Better Auth's
 * client is a proxy that turns a property path into a path on the wire —
 * `oauth2.publicClient({ query })` is `GET /oauth2/public-client`, `oauth2.consent({
 * … })` is `POST /oauth2/consent`, because a call with a body is a POST and
 * `toKebabCase` is the whole of the naming rule. `@better-auth/oauth-provider/client`
 * adds one thing on top of that: a fetch hook that reads `window.location.search` and
 * puts it on the body as `oauth_query` by magic. `pages/oauth/ConsentPage.tsx` passes
 * that argument itself, from a string the route handed it, which is a dependency this
 * package does not gain and a value a test can write down.
 *
 * **Same origin, always.** `basePath` is relative, so the cookie the API sets is
 * first-party in production (nginx serves both) and in development (the Vite proxy
 * forwards `/api` to :3000). `ARCHITECTURE.md` §Security: "Better Auth cookie:
 * `HttpOnly`, `Secure`, `SameSite=Lax`, same origin. No tokens in `localStorage`" —
 * there is nothing for this module to store, and it stores nothing.
 *
 * **Why `PortalAuthClient` is written out rather than inferred.** Better Auth's
 * client type is built from its plugins and names types inside its own `dist/`
 * that a declaration file here cannot reference (`TS2742`). Writing the surface
 * out fixes that, and buys two things worth more than the inference: it is the
 * list of everything this SPA asks Better Auth for — ten calls — and it is a
 * type a test can implement by hand, which is how `session.unit.test.ts` covers
 * the principal rules without a server or a cookie. The `authClient` assignment at
 * the bottom is the compile-time check that the real client still matches: a
 * signature Better Auth changes fails there rather than at runtime.
 *
 * The two `oauth2` calls are the one place that check is weaker than it looks, and
 * it is worth knowing why: they are served by the proxy rather than by a plugin, so
 * Better Auth's inferred type has no member for either and the assignment cannot
 * disagree with them. `modules/auth/oauth.test.ts` is what holds their shape — it
 * posts the same body to the same path against the real server.
 */

import { adminClient, magicLinkClient, organizationClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

import type { SocialProvider } from '@fareast/shared'

import type { Organization, SessionShape } from './principal.js'

/** `STACK.md`: "Mount at `/api/auth/*`". Relative, so it is whatever origin served us. */
export const AUTH_BASE_PATH = '/api/auth'

/**
 * A refusal, as much of one as this SPA reads.
 *
 * `status` is the field `auth/session.ts` `meansSignedOut` decides on, and it is
 * declared here because leaving it out is what R1-02 was: `@better-fetch/fetch`
 * puts `status` and `statusText` on **every** non-2xx answer, but a client that
 * only read `data` could not tell a `401` (the cookie is gone) from a `502` (the
 * gateway blinked) — both arrived as `{ data: null }` and both were read as
 * "signed out". One of those is a fact about the session and the other is a fact
 * about the network.
 *
 * `code` is Better Auth's machine word for the refusal, from the body beside
 * `message`. A screen that has its own sentence for a refusal looks it up by this —
 * `routes/login.tsx` `REFUSALS` is the one that does, for `NO_ACCOUNT_CODE` — and
 * falls back to `message` for everything else.
 */
export type AuthError = {
  readonly status?: number | undefined
  readonly statusText?: string | undefined
  readonly code?: string | undefined
  readonly message?: string | undefined
}

/**
 * Better Auth answers `{ data, error }` on every call and never throws — **except**
 * when the request itself fails. A DNS failure, an offline tab or an aborted
 * connection rejects the promise, because `@better-fetch/fetch` only converts a
 * thrown fetch into an answer under `catchAllError`, which Better Auth does not
 * set. `session.ts` is where both shapes become one decision.
 */
export type AuthResponse<T> = {
  readonly data?: T | null | undefined
  readonly error?: AuthError | null | undefined
}

/**
 * The whole of Better Auth this SPA uses.
 *
 * `getSession` returns rather less than Better Auth's own type does: two plugin
 * fields — `session.activeOrganizationId` (organization) and
 * `session.impersonatedBy` (admin) — are on the row and on the wire but are not on
 * the client's inferred type unless the whole server instance is inferred into the
 * client, which would make `apps/web` depend on `apps/api` at type level for every
 * build. They are declared optional here and read defensively in `principal.ts`.
 */
export type PortalAuthClient = {
  getSession(): Promise<AuthResponse<SessionShape | null>>
  signIn: {
    magicLink(input: { email: string; callbackURL?: string }): Promise<AuthResponse<unknown>>
    /**
     * Google or Microsoft. **Core, not a plugin** — no client plugin is added for
     * this, because `signIn.social` is part of Better Auth's own surface.
     *
     * It navigates the whole document rather than answering: the browser leaves for
     * the provider and comes back to `/api/auth/callback/:id`, which sets the cookie
     * and redirects to `callbackURL`. So the promise resolving means "the browser is
     * on its way", never "signed in" — `auth/actions.ts` says the same thing in
     * words, and `routes/login.tsx` never clears `busy` on the happy path.
     *
     * `errorCallbackURL` is where a refusal lands — `?error=signup_disabled` for an
     * address nobody invited, `?error=account_not_linked` for a Microsoft tenant that
     * has not verified the domain. Absolute, for the reason `callbackURL` is:
     * Better Auth resolves a relative one against **its** base, not the SPA's.
     */
    social(input: {
      provider: SocialProvider
      callbackURL?: string
      errorCallbackURL?: string
    }): Promise<AuthResponse<unknown>>
  }
  signOut(): Promise<AuthResponse<unknown>>
  organization: {
    list(): Promise<AuthResponse<readonly Organization[]>>
    setActive(input: { organizationId: string }): Promise<AuthResponse<unknown>>
    /**
     * `/organization/get-active-member-role`, not `/organization/get-active-member`.
     *
     * The second one reads `session.activeOrganizationId` and nothing else, so it
     * refuses with `NO_ACTIVE_ORGANIZATION` for the ordinary person who has signed
     * in once and belongs to one company — Better Auth does not set that column at
     * sign-in. This one takes the organisation as a query parameter, which is what
     * lets the SPA apply `resolvers.ts`'s rule 2 rather than write to the session
     * row to work around it. The role is all the principal needs.
     */
    getActiveMemberRole(input: {
      query: { organizationId: string }
    }): Promise<AuthResponse<{ role: string }>>
  }
  admin: {
    stopImpersonating(): Promise<AuthResponse<unknown>>
  }
  /**
   * The consent screen's two calls. `auth/oauth.ts` is the rest of the flow.
   *
   * Both are the OAuth provider's, and neither is in `DISABLED_OAUTH_PATHS` —
   * `modules/auth/auth.ts` keeps exactly the seven endpoints the flow walks and turns
   * the authorization-server administration API off, including
   * `/oauth2/public-client-prelogin`, which is the same read as the first of these
   * without a session behind it.
   */
  oauth2: {
    /**
     * What a client says it is called, for a screen that has to name it.
     *
     * **Attacker-controlled, and the screen says so.** Dynamic registration is open
     * (`.docs/reports/05-report.md`), so `client_name` is whatever the registrant
     * typed; it is offered as a label and never as an assurance, and
     * `ConsentPage.tsx` puts the redirect target above it for that reason. `logo_uri`
     * comes back too and is deliberately not read: the CSP is `img-src 'self' data:`,
     * so a remote logo would be a blocked request on the one screen that must not
     * look broken.
     */
    publicClient(input: { query: { client_id: string } }): Promise<AuthResponse<PublicOAuthClient>>
    /**
     * Yes or no, and where to go next.
     *
     * `oauth_query` is the signed query the provider redirected the browser with, and
     * it travels **verbatim** — `auth/oauth.ts` is the file that says why. The answer
     * carries the redirect either way: an accepted request comes back with the
     * authorization code on the client's own `redirect_uri`, a refused one with
     * `error=access_denied` on the same address, and both are a full document load
     * out of this SPA rather than a router navigation.
     */
    consent(input: { accept: boolean; oauth_query: string }): Promise<AuthResponse<OAuthRedirect>>
  }
}

/** `GET /oauth2/public-client` — the fields a consent screen may show. */
export type PublicOAuthClient = {
  readonly client_id: string
  readonly client_name?: string | null | undefined
  readonly client_uri?: string | null | undefined
}

/**
 * Where the provider says to send the browser next.
 *
 * Two spellings because the endpoint has two: `handleRedirect` answers `{ redirect,
 * url }` to a fetch and the documented body is `{ redirect_uri }`. Reading both is
 * what `modules/auth/oauth.test.ts` already does against the real server.
 */
export type OAuthRedirect = {
  readonly redirect?: boolean | undefined
  readonly url?: string | undefined
  readonly redirect_uri?: string | undefined
}

export function createPortalAuthClient(fetchImpl?: typeof fetch): PortalAuthClient {
  const client = createAuthClient({
    basePath: AUTH_BASE_PATH,
    plugins: [organizationClient(), magicLinkClient(), adminClient()],
    ...(fetchImpl === undefined ? {} : { fetchOptions: { customFetchImpl: fetchImpl } }),
  })

  /*
   * The one cast in this file, and it is the `oauth2` pair.
   *
   * Better Auth builds a client's *type* from its plugins and its *behaviour* from a
   * proxy that will call any path — so the eight calls above are inferred and checked
   * against `PortalAuthClient` by this assignment, and the two below work at runtime
   * while being absent from the inferred type. The alternative is
   * `@better-auth/oauth-provider/client`, whose only substance is a fetch hook that
   * reads `window.location.search` behind the caller's back; the consent screen passes
   * that string itself, so the package would be a dependency bought entirely for a
   * type.
   *
   * It is an **intersection** rather than `as unknown as`, which is the difference
   * between adding two calls and abandoning the check on the other eight: the return
   * type still has to be satisfied by what `createAuthClient` inferred, so a signature
   * Better Auth changes on `signIn.magicLink` still fails here. What the cast does
   * give up is the proxy's naming rule — that `oauth2.publicClient` is
   * `/oauth2/public-client` and that a call with a body is a POST. `client.test.ts`
   * §the two OAuth calls buys that back by driving the real client through a stub
   * fetch and asserting the request it made.
   */
  return client as typeof client & Pick<PortalAuthClient, 'oauth2'>
}

/**
 * The one the browser uses.
 *
 * A module-level singleton, unlike everything else in this repository, and
 * deliberately: `createAuthClient` builds nanostores and a cookie cache, so two
 * clients would be two of each and a `signOut()` through one would leave the other
 * holding a session it thinks is live. There is one browser and one cookie; there
 * is one client. Everything that consumes it takes it as an argument.
 */
export const authClient: PortalAuthClient = createPortalAuthClient()
